import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@codes/ui-kit/components/ui/button';
import { Card } from '@codes/ui-kit/components/ui/card';
import { Image } from '@codes/ui-kit/components/ui/image';
import { ProductView } from '@lemoncloud/codes-goods-api';
import { ListResult } from '@lemoncloud/codes-goods-api/dist/cores/types';
import { useQueryClient } from '@tanstack/react-query';
import {
    AIStudioDialog,
    ChildrenSheet,
    ProductCard,
    ServiceEventLogDialog,
    ServiceSettingsDialog,
    SortSelect,
    StereoSelect,
} from '@web/app/features/home/components';
import {
    useDeleteWithConfirmation,
    useDialogState,
    useProductFiltering,
    useProductService,
    useWorkspaceService,
} from '@web/app/features/home/hooks';
import { getStereoDisplayName, useSelectedStore, useWorkspaceNavigation } from '@web/app/shared';
import { Plus } from 'lucide-react';

import { ListEmptyImage } from '@codes/assets';
import { productsKey, useAutoDeployProduct, useDeleteProduct } from '@codes/product';
import { SharedSpinner, toast } from '@codes/ui-kit';

export const ProductListPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { project, workspace } = useSelectedStore();
    const { myProducts, myProductsQueryResult, areMyProductsEmpty } = useProductService();

    const isProWorkspace = workspace && workspace.stereo === 'pro';
    const isLoading = myProductsQueryResult.isFetching || myProductsQueryResult.isRefetching;
    const isInitialLoading = myProductsQueryResult.isLoading && !myProductsQueryResult.data;

    // Use custom hooks for filtering and dialog state
    const { sortType, setSortType, stereo, setStereo, stereos, filteredProducts, filteredStereos } =
        useProductFiltering(myProducts);

    const settingsDialog = useDialogState<string>();
    const eventLogDialog = useDialogState<string>();
    const [isAIStudioDialogOpen, setAIStudioDialogOpen] = useState(false);
    const [childrenSheetProduct, setChildrenSheetProduct] = useState<ProductView | null>(null);

    const { workspaceFromServer } = useWorkspaceService();

    const { navigateToProjectPath } = useWorkspaceNavigation({ workspace: workspaceFromServer, project });

    const addServiceButtonTapped = () => {
        navigateToProjectPath('/catalogs');
    };

    const viewServiceSettingButtonTapped = (productId?: string) => {
        if (!productId) {
            return;
        }
        settingsDialog.open(productId);
    };

    const viewServiceEventLogButtonTapped = (productId?: string) => {
        if (!productId) {
            return;
        }
        eventLogDialog.open(productId);
    };

    const viewProductDetailButtonTapped = (product: ProductView) => {
        if (!workspace || !project) {
            return;
        }
        navigate(`/w/${workspace.id}/p/${project.id}/products/${product.id}`);
    };

    const { mutateAsync: deleteProduct } = useDeleteProduct();
    const { mutateAsync: autoDeployProduct, isPending: isAutoDeploying, currentProductId } = useAutoDeployProduct();
    const queryClient = useQueryClient();

    const { confirmAndDelete } = useDeleteWithConfirmation({
        onDelete: deleteProduct,
        confirmMessage: t('serviceSetting.confirmServiceDeletion'),
        onSuccess: deletedProduct => {
            toast({
                description: t('serviceSetting.deleteSuccess', { productName: deletedProduct?.name }),
            });

            queryClient.setQueryData<ListResult<ProductView>>(productsKey.list({ projectId: project?.id }), oldData => {
                if (!oldData) {
                    return oldData;
                }
                return {
                    ...oldData,
                    list: oldData.list.filter(product => product.id !== deletedProduct.id),
                    data: oldData.data.filter(product => product.id !== deletedProduct.id),
                    total: (oldData.total || 1) - 1,
                };
            });
        },
    });

    const viewServiceDeleteButtonTapped = async (productId?: string) => {
        if (!productId) {
            return;
        }
        await confirmAndDelete(productId);
    };

    const reDeployProductButtonTapped = async (productId?: string) => {
        if (!productId) {
            return;
        }

        try {
            await autoDeployProduct(productId);

            queryClient.setQueryData<ListResult<ProductView>>(productsKey.list({ projectId: project?.id }), oldData => {
                if (!oldData) {
                    return oldData;
                }
                return {
                    ...oldData,
                    list: oldData.list.map(product =>
                        product.id === productId ? { ...product, status: 'idle', progress$: undefined } : product
                    ),
                    data: oldData.data.map(product =>
                        product.id === productId ? { ...product, status: 'idle', progress$: undefined } : product
                    ),
                };
            });

            toast({
                title: t('product.frontend.deploy.requestSuccess'),
                description: t('product.frontend.deploy.requestSuccessDescription', {
                    productName: myProducts.find(p => p.id === productId)?.name || '',
                }),
            });
        } catch (e) {
            console.error(e, 'Error while redeploying product');
        }
    };

    const openChildrenSheet = (product: ProductView) => {
        setChildrenSheetProduct(product);
    };

    const closeChildrenSheet = () => {
        setChildrenSheetProduct(null);
    };

    // Loading state
    if (isInitialLoading) {
        return (
            <div className="w-full py-10 px-[43px] max-md:p-5">
                <div className="flex items-center justify-between mb-4">
                    <div className="text-[26px] font-medium">My service</div>
                    <div className="flex gap-2">
                        {isProWorkspace && (
                            <Button
                                onClick={() => setAIStudioDialogOpen(true)}
                                variant="outline"
                                className="h-11 rounded-full gap-[6px]"
                            >
                                <Plus size={16} /> {t('aiStudio.add')}
                            </Button>
                        )}
                        <Button onClick={addServiceButtonTapped} className="h-11 rounded-full gap-[6px]">
                            <Plus size={16} /> {t('project.addService')}
                        </Button>
                    </div>
                </div>
                <div className="flex items-center gap-3 mb-10">
                    <SortSelect value={sortType} onChange={setSortType} />
                    <StereoSelect value={stereo} onChange={setStereo} stereos={stereos} />
                    <div className="text-gray-400">|</div>
                    <div className="text-point text-[15px] font-medium">Total --</div>
                </div>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(350px,1fr))] max-xs:grid-cols-1 gap-8 max-md:gap-6">
                    {[...Array(6)].map((_, i) => (
                        <Card key={i} className="w-full p-5 h-[280px] flex flex-col">
                            <div className="animate-pulse flex-1 flex flex-col">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="w-16 h-6 bg-gray-200 rounded-full"></div>
                                    <div className="w-6 h-6 bg-gray-200 rounded"></div>
                                </div>
                                <div className="flex items-center gap-4 mb-4 flex-1">
                                    <div className="w-[50px] h-[50px] bg-gray-200 rounded-[14px] flex-shrink-0"></div>
                                    <div className="flex-1">
                                        <div className="h-4 bg-gray-200 rounded mb-2 w-2/3"></div>
                                        <div className="h-5 bg-gray-200 rounded w-1/2"></div>
                                    </div>
                                </div>
                                <div className="space-y-2 mb-4">
                                    <div className="h-3 bg-gray-200 rounded w-full"></div>
                                    <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                                </div>
                                <div className="flex gap-2 mt-auto">
                                    <div className="h-6 bg-gray-200 rounded w-16"></div>
                                    <div className="h-6 bg-gray-200 rounded w-16"></div>
                                    <div className="h-6 bg-gray-200 rounded w-16"></div>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            </div>
        );
    }

    // Error state
    if (myProductsQueryResult.error) {
        return (
            <div className="w-full py-10 px-[43px] max-md:p-5">
                <div className="flex items-center justify-between mb-4">
                    <div className="text-[26px] font-medium">My service</div>
                    <div className="flex gap-2">
                        {isProWorkspace && (
                            <Button
                                onClick={() => setAIStudioDialogOpen(true)}
                                variant="outline"
                                className="h-11 rounded-full gap-[6px]"
                            >
                                <Plus size={16} /> {t('aiStudio.add')}
                            </Button>
                        )}
                        <Button onClick={addServiceButtonTapped} className="h-11 rounded-full gap-[6px]">
                            <Plus size={16} /> {t('project.addService')}
                        </Button>
                    </div>
                </div>
                <div className="flex flex-col items-center justify-center py-20">
                    <div className="text-error text-lg font-medium mb-4">Failed to load services</div>

                    <Button
                        onClick={() => myProductsQueryResult.refetch()}
                        variant="outline"
                        disabled={myProductsQueryResult.isRefetching}
                    >
                        {isLoading ? <SharedSpinner /> : t('project.error.retry')}
                    </Button>
                </div>
            </div>
        );
    }

    // Empty state
    if (areMyProductsEmpty || filteredProducts.length === 0) {
        return (
            <div className="w-full py-10 px-[43px] max-md:p-5">
                <div className="text-[26px] font-medium">My Service</div>
                <div className="flex-1 flex flex-col justify-center items-center mt-10">
                    <Image className="max-w-[140px]" src={ListEmptyImage} />
                    <div className="text-[26px] font-medium text-gray-black mb-3 mt-[19px]">
                        No requested services found.
                    </div>
                    <div className="text-[20px] font-medium text-gray-700 mb-[22px] text-center">
                        Please add a service!
                    </div>
                    <div className="flex gap-2">
                        {isProWorkspace && (
                            <Button
                                onClick={() => setAIStudioDialogOpen(true)}
                                variant="outline"
                                className="h-11 rounded-full gap-[6px]"
                            >
                                <Plus size={16} />
                                {t('aiStudio.add')}
                            </Button>
                        )}
                        <Button onClick={addServiceButtonTapped} className="h-11 rounded-full gap-[6px]">
                            <Plus size={16} />
                            {t('project.addService')}
                        </Button>
                    </div>
                </div>
                <AIStudioDialog open={isAIStudioDialogOpen} onOpenChange={setAIStudioDialogOpen} />
            </div>
        );
    }

    return (
        <div className="w-full py-10 px-[43px] max-md:p-5">
            <div className="flex items-center justify-between mb-4">
                <div className="text-[26px] font-medium">My service</div>
                <div className="flex gap-2">
                    {isProWorkspace && (
                        <Button
                            onClick={() => setAIStudioDialogOpen(true)}
                            variant="outline"
                            className="h-11 rounded-full gap-[6px]"
                        >
                            <Plus size={16} /> {t('aiStudio.add')}
                        </Button>
                    )}
                    <Button onClick={addServiceButtonTapped} className="h-11 rounded-full gap-[6px]">
                        <Plus size={16} /> {t('project.addService')}
                    </Button>
                </div>
            </div>
            <div className="flex items-center gap-4 mb-10">
                <SortSelect value={sortType} onChange={setSortType} />
                <StereoSelect value={stereo} onChange={setStereo} stereos={stereos} />
                <div className="text-gray-400">|</div>
                <div className="text-point text-[15px] font-medium">Total {filteredProducts?.length || 0}</div>
            </div>

            <div className="flex flex-col gap-y-10">
                {filteredStereos.map(stereo => {
                    const stereoProducts = filteredProducts.filter(product => product.catalog$?.stereo === stereo);

                    return (
                        <div key={stereo} className="flex flex-col gap-y-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="text-lg text-point font-semibold">
                                        {getStereoDisplayName(stereo)}
                                    </div>
                                    <div className="text-sm text-gray-500 font-medium">({stereoProducts.length})</div>
                                </div>
                            </div>
                            <div className="grid grid-cols-[repeat(auto-fill,minmax(350px,1fr))] max-xs:grid-cols-1 gap-8 max-md:gap-6">
                                {stereoProducts.map(product => {
                                    const isAIStudio = product.catalog$?.stereo === 'aistudio';

                                    return (
                                        <ProductCard
                                            key={product.id}
                                            product={product}
                                            isDeploying={isAutoDeploying && currentProductId === product.id}
                                            onProductClick={() => viewProductDetailButtonTapped(product)}
                                            onSettingsClick={() => viewServiceSettingButtonTapped(product.id)}
                                            onEventLogsClick={() => viewServiceEventLogButtonTapped(product.id)}
                                            onRedeploy={() => reDeployProductButtonTapped(product.id)}
                                            onDelete={() => viewServiceDeleteButtonTapped(product.id)}
                                            showChildrenButton={isAIStudio}
                                            onViewChildrenClick={() => openChildrenSheet(product)}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            {settingsDialog.isOpen && settingsDialog.data && (
                <ServiceSettingsDialog
                    open={settingsDialog.isOpen}
                    onOpenChange={open => !open && settingsDialog.close()}
                    productId={settingsDialog.data}
                />
            )}

            {eventLogDialog.isOpen && eventLogDialog.data && (
                <ServiceEventLogDialog
                    productId={eventLogDialog.data}
                    open={eventLogDialog.isOpen}
                    setOpen={open => !open && eventLogDialog.close()}
                />
            )}

            <AIStudioDialog open={isAIStudioDialogOpen} onOpenChange={setAIStudioDialogOpen} />

            {/* Children Sheet */}
            {childrenSheetProduct && (
                <ChildrenSheet
                    parentProduct={childrenSheetProduct}
                    open={!!childrenSheetProduct}
                    onOpenChange={open => !open && closeChildrenSheet()}
                    isAutoDeploying={isAutoDeploying}
                    currentProductId={currentProductId}
                    onProductClick={viewProductDetailButtonTapped}
                    onSettingsClick={viewServiceSettingButtonTapped}
                    onEventLogsClick={viewServiceEventLogButtonTapped}
                    onRedeploy={reDeployProductButtonTapped}
                    onDelete={viewServiceDeleteButtonTapped}
                />
            )}
        </div>
    );
};
